import { useEffect, useState } from "react";
import { Button } from "../../../ui/button";
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import { SwitchFieldset } from "ui/switchFieldset";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../../ui/select";
import { Switch } from "../../../ui/switch";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { validatePhoneNumberInput } from "utils/commonutils";
import { useScreenPermission } from "../../../hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";

export interface VenueFormData {
  venueName: string;
  description: string;
  contactNo: string;
  email: string;
  timeZone: string;
  isActive: boolean;
}

interface VenueMasterFormPageProps {
  onBack: () => void;
  editMode?: boolean;
  initialData?: VenueFormData;
  timeZones: string[];
  onSaveVenue: (data: VenueFormData) => void;
}

export const VenueMasterFormPage = ({
  onBack,
  editMode = false,
  initialData,
  timeZones,
  onSaveVenue,
}: VenueMasterFormPageProps) => {
  const { setPageHeader } = usePageHeader();
  const { screenName, icon: ScreenIcon} = useScreenPermission();

  const [formData, setFormData] = useState<VenueFormData>(
    initialData || {
      venueName: "",
      description: "",
      contactNo: "",
      email: "",
      timeZone: timeZones?.[0] || "",
      isActive: true,
    }
  );

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Update form field
  const updateField = (
    field: keyof VenueFormData,
    value: VenueFormData[keyof VenueFormData]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-4 w-4" />,
      onBack,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenName, ScreenIcon]);

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    const nameRegex = /^[A-Za-z ]+$/;
    const emailRegex = /^[A-Za-z0-9@.]+$/;

    if (!formData.venueName.trim()) {
      errors.venueName = "Venue name is required";
    } else if (formData.venueName.trim().length > 20) {
      errors.venueName = "Venue name cannot exceed 20 characters";
    } else if (!nameRegex.test(formData.venueName.trim())) {
      errors.venueName = "Venue name can contain only letters and spaces";
    }

    if (
      formData.description &&
      !nameRegex.test(formData.description.trim())
    ) {
      errors.description =
        "Description can contain only letters and spaces";
    }

    if (!formData.contactNo.trim()) {
      errors.contactNo = "Contact number is required";
    }

    if (!formData.timeZone) {
      errors.timeZone = "Time zone is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(formData.email)) {
      errors.email =
        "Email can contain only letters, numbers, '.', and '@'";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  }

  // Save venue
  const handleSave = () => {
    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }

    onSaveVenue(formData);
  };

  return (
    <div className="space-y-6">
      {/* Form Card */}
      <div className="space-y-6">
        {/* Venue Name */}
        <div className="space-y-2">
          <InputFieldset
            label="Venue Name"
            required
            placeholder="Enter venue name (e.g., Karbala)"
            value={formData.venueName}
            maxLength={20}
            onChange={(e) => {
              const value = e.target.value
                .replace(/[^A-Za-z ]/g, "")
                .slice(0, 20);
              updateField("venueName", value)
            }}
            className={
              validationErrors.venueName
                ? "border-red-500"
                : "border-gray-300"
            }
          />
          {validationErrors.venueName && (
            <p className="text-sm text-red-600">
              {validationErrors.venueName}
            </p>
          )}
        </div>

        {/* Description */}
        <div className="space-y-2">
          <InputFieldset
            label="Description"
            placeholder="Enter venue description..."
            value={formData.description}
            onChange={(e) => {
              const value = e.target.value.replace(/[^A-Za-z ]/g, "");
              updateField("description", value);
            }}
            className="border-gray-300"
          />
          {validationErrors.description && (
            <p className="text-sm text-red-600">
              {validationErrors.description}
            </p>
          )}
        </div>

        {/* Contact & Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact */}
          <div className="space-y-2">
            <InputFieldset
              label="Contact Number"
              required
              placeholder="+964 XX XXX XXXX"
              value={formData.contactNo}
              onChange={(e) => {
                const value = validatePhoneNumberInput(e.target.value)
                updateField("contactNo", value)
              }}
              className={
                validationErrors.contactNo
                  ? "border-red-500"
                  : "border-gray-300"
              }
            />
            {validationErrors.contactNo && (
              <p className="text-sm text-red-600">
                {validationErrors.contactNo}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <InputFieldset
              label="Email Address"
              required
              type="email"
              placeholder="venue@example.com"
              value={formData.email}
              onChange={(e) => {
                const value = e.target.value.replace(/[^A-Za-z0-9@.]/g, "");
                updateField("email", value);
              }}
              className={
                validationErrors.email
                  ? "border-red-500"
                  : "border-gray-300"
              }
            />
            {validationErrors.email && (
              <p className="text-sm text-red-600">
                {validationErrors.email}
              </p>
            )}
          </div>
        </div>

        {/* Time Zone + Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Time Zone */}
          <div className="space-y-2">
            <InputFieldset
              label="Max Occupancy"
              required
              type="number"
              placeholder="Enter max occupancy"
              // value={formData.maxOccupancy}
              // onChange={(e) => updateField("maxOccupancy", e.target.value)}
              className={
                validationErrors.maxOccupancy
                  ? "border-red-500"
                  : "border-gray-300"
              }
            />
            {validationErrors.maxOccupancy && (
              <p className="text-sm text-red-600">
                {validationErrors.maxOccupancy}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <SelectFieldset label="Time Zone" >
              <Select
                value={formData.timeZone}
                onValueChange={(value) => updateField("timeZone", value)}
              >
                <SelectTriggerUI className="border-gray-300">
                  <SelectValue placeholder="Select Time Zone" />
                </SelectTriggerUI>

                <SelectContent>
                  {timeZones.map((tz) => (
                    <SelectItem key={tz} value={tz}>
                      {tz}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SelectFieldset>

            {validationErrors.timeZone && (
              <p className="text-sm text-red-600">
                {validationErrors.timeZone}
              </p>
            )}
          </div>
          {/* Status */}
          <div className="space-y-2">
            <SwitchFieldset label="Status">
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    updateField("isActive", checked)
                  }
                />
                <span className="ml-3 text-sm">
                  {formData.isActive ? (
                    <span className="text-green-700">Active</span>
                  ) : (
                    <span className="text-gray-500">Inactive</span>
                  )}
                </span>
              </div>
            </SwitchFieldset>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end" style={{ marginTop: "10px" }}>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onBack}
            className="border-gray-300"
          >
            Cancel
          </Button>

          <Button
            onClick={handleSave}
            style={{ backgroundColor: "#5B9BD5" }}
            className="text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {editMode ? "Update" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};
